import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Plus, Search, X, Edit2, Trash2, SlidersHorizontal,
  ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Upload, ArrowRightLeft
} from 'lucide-react';
import StatusBadge from '../components/StatusBadge';
import LancamentoModal from '../components/LancamentoModal';
import ImportacaoCSVModal from '../components/ImportacaoCSVModal';
import TransferenciaModal from '../components/TransferenciaModal';
import api from '../utils/api';
import { formatCurrency, formatDate, isAtrasado, statusEfetivo } from '../utils/format';

const TIPO_COLORS = { RECEITA: '#339933', CUSTO: '#c99a1a', DESPESA: '#d72d21', TRANSFERENCIA: '#6366f1' };
const PAGE_SIZE = 50;

const emptyFilters = {
  data_inicio: '', data_fim: '', tipo: '', status: '',
  categoria: '', forma_pagamento: '', valor_min: '', valor_max: '', descricao: '',
  carteira_id: '',
};

export default function Lancamentos() {
  const [searchParams] = useSearchParams();
  const urlBusca = searchParams.get('busca') || searchParams.get('descricao') || '';
  const urlTipo = ['RECEITA', 'DESPESA', 'CUSTO'].includes(searchParams.get('tipo')) ? searchParams.get('tipo') : '';

  const [lancamentos, setLancamentos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [carteiras, setCarteiras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(() => ({
    ...emptyFilters,
    descricao: urlBusca || '',
    tipo: urlTipo,
  }));
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editLanc, setEditLanc] = useState(null);
  const [showFilters, setShowFilters] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showTransferenciaModal, setShowTransferenciaModal] = useState(searchParams.get('acao') === 'transferencia');

  useEffect(() => {
    if (urlBusca) {
      setFilters(prev => ({ ...prev, descricao: urlBusca }));
    }
  }, [urlBusca]);

  useEffect(() => {
    if (urlTipo) {
      setFilters(prev => ({ ...prev, tipo: urlTipo }));
    }
  }, [urlTipo]);

  useEffect(() => {
    api.get('/categorias').then(r => setCategorias(r.data)).catch(() => {});
    api.get('/carteiras').then(r => setCarteiras(r.data)).catch(() => {});
  }, []);

  const fetchLancamentos = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const res = await api.get(`/lancamentos?${params.toString()}`);
      setLancamentos(res.data);
      setPage(1);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchLancamentos(); }, [fetchLancamentos]);

  const setFilter = (k, v) => {
    setFilters(p => ({ ...p, [k]: v }));
    setPage(1);
  };

  const clearFilters = () => { setFilters(emptyFilters); setPage(1); };

  const handleDelete = async (id) => {
    try {
      const lanc = lancamentos.find(l => l.id === id);
      await api.delete(`/lancamentos/${id}`);
      if (lanc?.transferId) {
        // Remove both sides of the transfer
        setLancamentos(p => p.filter(l => l.transferId !== lanc.transferId));
      } else {
        setLancamentos(p => p.filter(l => l.id !== id));
      }
      setDeleteConfirm(null);
    } catch (err) {
      console.error(err);
    }
  };

  const totalPages = Math.max(1, Math.ceil(lancamentos.length / PAGE_SIZE));
  const paginated = lancamentos.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const pageNumbers = () => {
    const pages = [];
    const range = 2;
    for (let i = Math.max(1, page - range); i <= Math.min(totalPages, page + range); i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="space-y-4 sm:space-y-6 page-enter max-w-full overflow-x-hidden pb-10" data-testid="lancamentos-page">
      {/* Header Responsivo */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Lançamentos
          </h1>
          <p className="text-slate-500 dark:text-slate-400 text-xs sm:text-sm mt-0.5">
            {lancamentos.length} {lancamentos.length === 1 ? 'registro' : 'registros'} encontrados
          </p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto">
          <div className="grid grid-cols-2 gap-2 w-full sm:w-auto">
            <button
              onClick={() => setShowImportModal(true)}
              className="fin-btn-secondary text-[11px] sm:text-xs flex items-center justify-center gap-1.5 py-2 px-2 text-center truncate min-w-0"
              title="Importar CSV ou PDF"
            >
              <Upload size={14} className="flex-shrink-0" />
              <span className="truncate">Importar CSV ou PDF</span>
            </button>
            <button
              onClick={() => setShowTransferenciaModal(true)}
              className="fin-btn-secondary text-[11px] sm:text-xs flex items-center justify-center gap-1.5 py-2 px-2 text-center truncate min-w-0"
              title="Transferência entre contas"
            >
              <ArrowRightLeft size={14} className="flex-shrink-0" />
              <span className="truncate">Transferência</span>
            </button>
          </div>
          <button
            onClick={() => { setEditLanc(null); setShowModal(true); }}
            className="fin-btn-primary text-xs flex items-center justify-center gap-1.5 py-2.5 px-3.5 w-full sm:w-auto shadow-sm"
            data-testid="novo-lancamento-btn"
          >
            <Plus size={15} className="flex-shrink-0" />
            <span className="font-bold">Novo Lançamento</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="fin-card p-4 sm:p-5" data-testid="lancamentos-filters">
        <button
          className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200 w-full mb-0"
          onClick={() => setShowFilters(v => !v)}
          data-testid="toggle-lancamentos-filters-btn"
        >
          <SlidersHorizontal size={16} className="text-[#0147a0]" />
          <span>Filtros</span>
          <span className="ml-2 text-xs font-normal text-slate-400">
            {Object.values(filters).filter(Boolean).length > 0 ? `(${Object.values(filters).filter(Boolean).length} ativos)` : ''}
          </span>
          {showFilters ? <ChevronUp size={16} className="ml-auto text-slate-400" /> : <ChevronDown size={16} className="ml-auto text-slate-400" />}
        </button>
        {showFilters && (
          <div className="mt-4 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <div>
                <label className="fin-label">Data Início</label>
                <input type="date" value={filters.data_inicio}
                  onChange={e => setFilter('data_inicio', e.target.value)}
                  className="fin-input w-full" data-testid="filter-lanc-data-inicio" />
              </div>
              <div>
                <label className="fin-label">Data Fim</label>
                <input type="date" value={filters.data_fim}
                  onChange={e => setFilter('data_fim', e.target.value)}
                  className="fin-input w-full" data-testid="filter-lanc-data-fim" />
              </div>
              <div>
                <label className="fin-label">Tipo</label>
                <select value={filters.tipo} onChange={e => setFilter('tipo', e.target.value)}
                  className="fin-input w-full" data-testid="filter-lanc-tipo">
                  <option value="">Todos</option>
                  <option>RECEITA</option>
                  <option>DESPESA</option>
                  <option>CUSTO</option>
                  <option value="TRANSFERENCIA">TRANSFERÊNCIA</option>
                </select>
              </div>
              <div>
                <label className="fin-label">Status</label>
                <select value={filters.status} onChange={e => setFilter('status', e.target.value)}
                  className="fin-input w-full" data-testid="filter-lanc-status">
                  <option value="">Todos</option>
                  <option value="pago">Pago</option>
                  <option value="pendente">Pendente</option>
                  <option value="atrasado">Atrasado</option>
                </select>
              </div>
              <div>
                <label className="fin-label">Categoria</label>
                <select value={filters.categoria} onChange={e => setFilter('categoria', e.target.value)}
                  className="fin-input w-full" data-testid="filter-lanc-categoria">
                  <option value="">Todas</option>
                  {categorias.map(c => (
                    <option key={c.id} value={c.id}>{c.nome}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="fin-label">Forma de Pagamento</label>
                <select value={filters.forma_pagamento} onChange={e => setFilter('forma_pagamento', e.target.value)}
                  className="fin-input w-full" data-testid="filter-lanc-forma">
                  <option value="">Todas</option>
                  {['PIX', 'Boleto', 'Cartão', 'TED-DOC', 'Dinheiro'].map(f => (
                    <option key={f}>{f}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="fin-label">Carteira</label>
                <select value={filters.carteira_id} onChange={e => setFilter('carteira_id', e.target.value)}
                  className="fin-input w-full" data-testid="filter-lanc-carteira">
                  <option value="">Todas</option>
                  {carteiras.map(c => (
                    <option key={c.id} value={c.id}>{c.nome}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="fin-label">Valor Mínimo</label>
                <input type="number" min="0" step="0.01" value={filters.valor_min}
                  onChange={e => setFilter('valor_min', e.target.value)}
                  className="fin-input w-full" placeholder="0,00" data-testid="filter-lanc-valor-min" />
              </div>
              <div className="sm:col-span-2 lg:col-span-1">
                <label className="fin-label">Valor Máximo</label>
                <input type="number" min="0" step="0.01" value={filters.valor_max}
                  onChange={e => setFilter('valor_max', e.target.value)}
                  className="fin-input w-full" placeholder="99999,00" data-testid="filter-lanc-valor-max" />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4 pt-1">
              <div className="flex-1 relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={filters.descricao}
                  onChange={e => setFilter('descricao', e.target.value)}
                  className="fin-input pl-8 w-full"
                  placeholder="Buscar por descrição..."
                  data-testid="filter-lanc-descricao"
                />
              </div>
              <button
                onClick={clearFilters}
                className="fin-btn-secondary flex items-center justify-center gap-2 text-xs py-2 px-3 text-rose-600 hover:text-rose-700 hover:bg-rose-50 border-rose-200"
                data-testid="clear-lanc-filters-btn"
              >
                <X size={14} />
                <span>Limpar Filtros</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Lista / Tabela de Lançamentos */}
      <div className="fin-card overflow-hidden" data-testid="lancamentos-table">
        {/* Visualização Mobile: Cards nativos e centralizados */}
        <div className="block md:hidden divide-y divide-slate-100 dark:divide-slate-800">
          {loading ? (
            Array(4).fill(0).map((_, i) => (
              <div key={i} className="p-4 space-y-2 animate-pulse">
                <div className="h-4 bg-slate-100 dark:bg-slate-800 rounded w-1/3" />
                <div className="h-5 bg-slate-100 dark:bg-slate-800 rounded w-3/4" />
                <div className="h-4 bg-slate-100 dark:bg-slate-800 rounded w-1/2" />
              </div>
            ))
          ) : paginated.length === 0 ? (
            <div className="py-12 px-4 text-center text-slate-400 text-sm">
              Nenhum lançamento encontrado.
            </div>
          ) : (
            paginated.map(l => {
              const isTransfer = l.tipo === 'TRANSFERENCIA';
              const corValor = isTransfer
                ? (l.transferDirecao === 'saida' ? '#ef4444' : '#22c55e')
                : TIPO_COLORS[l.tipo];

              return (
                <div
                  key={l.id}
                  className="p-4 space-y-2.5 hover:bg-slate-50/60 dark:hover:bg-slate-800/40 transition-colors"
                  data-testid={`lancamento-mobile-card-${l.id}`}
                >
                  {/* Topo do card: Data, Categoria e Tipo */}
                  <div className="flex items-center justify-between text-xs text-slate-400">
                    <div className="flex items-center gap-2 truncate">
                      <span className="font-semibold text-slate-500 dark:text-slate-400">{formatDate(l.dataCompetencia)}</span>
                      <span>•</span>
                      <span className="truncate">{l.categoria || 'Geral'}</span>
                    </div>
                    {isTransfer ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-50 text-indigo-600 border border-indigo-100">
                        <ArrowRightLeft size={10} />
                        {l.transferDirecao === 'saida' ? 'Transf. saída' : 'Transf. entrada'}
                      </span>
                    ) : (
                      <StatusBadge type="tipo" value={l.tipo} />
                    )}
                  </div>

                  {/* Meio: Descrição e Valor */}
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-100 leading-snug flex-1">
                      {l.descricao}
                    </p>
                    <p className="text-base font-extrabold whitespace-nowrap text-right" style={{ color: corValor }}>
                      {isTransfer && l.transferDirecao === 'saida' ? '-' : ''}
                      {formatCurrency(l.valor)}
                    </p>
                  </div>

                  {/* Base: Status, Carteira e Ações */}
                  <div className="flex items-center justify-between gap-2 pt-1 border-t border-slate-50 dark:border-slate-800/60 text-xs">
                    <div className="flex items-center gap-2 flex-wrap min-w-0">
                      <StatusBadge type="status" value={statusEfetivo(l)} />
                      {l.carteiraNome && (
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-white font-semibold text-[10px] truncate max-w-[120px]"
                          style={{ background: l.carteiraCor || '#64748b' }}
                        >
                          {l.carteiraNome}
                        </span>
                      )}
                      <span className="text-slate-400 text-[11px]">{l.formaPagamento}</span>
                    </div>

                    {/* Ações diretas e acessíveis no mobile sem hover */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {!isTransfer && (
                        <button
                          onClick={() => { setEditLanc(l); setShowModal(true); }}
                          className="p-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-blue-50 text-slate-600 dark:text-slate-300 hover:text-[#0147a0] transition-colors"
                          title="Editar"
                        >
                          <Edit2 size={13} />
                        </button>
                      )}
                      <button
                        onClick={() => setDeleteConfirm(l.id)}
                        className="p-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-red-50 text-slate-600 dark:text-slate-300 hover:text-red-500 transition-colors"
                        title="Excluir"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Visualização Desktop: Tabela Tradicional de 9 colunas */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F0F4FF] dark:bg-slate-800/60 border-b border-slate-100 dark:border-slate-800">
                {['Data', 'Tipo', 'Categoria', 'Descrição', 'Valor', 'Status', 'Forma Pgto', 'Carteira', 'Ações'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-slate-400">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 dark:divide-slate-800/60">
              {loading ? (
                Array(8).fill(0).map((_, i) => (
                  <tr key={i}>
                    {Array(9).fill(0).map((_, j) => (
                      <td key={j} className="px-4 py-3">
                        <div className="h-3 bg-slate-100 dark:bg-slate-800 rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : paginated.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center text-slate-400 text-sm">
                    Nenhum lançamento encontrado.
                  </td>
                </tr>
              ) : (
                paginated.map(l => (
                  <tr
                    key={l.id}
                    className="hover:bg-[#F0F4FF]/60 dark:hover:bg-slate-800/40 transition-colors group"
                    data-testid={`lancamento-row-${l.id}`}
                  >
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300 whitespace-nowrap">{formatDate(l.dataCompetencia)}</td>
                    <td className="px-4 py-3">
                      {l.tipo === 'TRANSFERENCIA' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-indigo-50 text-indigo-600 border border-indigo-100">
                          <ArrowRightLeft size={10} />
                          {l.transferDirecao === 'saida' ? 'Transf. saída' : 'Transf. entrada'}
                        </span>
                      ) : (
                        <StatusBadge type="tipo" value={l.tipo} />
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300 max-w-[120px] truncate">
                      {l.tipo === 'TRANSFERENCIA' ? (
                        <span className="text-xs text-slate-500">
                          {l.transferDirecao === 'saida' ? `→ ${l.transferCarteiraParNome}` : `← ${l.transferCarteiraParNome}`}
                        </span>
                      ) : (l.categoria || '-')}
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-800 dark:text-slate-100 max-w-[180px]">
                      <span className="truncate block">{l.descricao}</span>
                    </td>
                    <td className="px-4 py-3 font-bold whitespace-nowrap" style={{ color: l.tipo === 'TRANSFERENCIA' ? (l.transferDirecao === 'saida' ? '#ef4444' : '#22c55e') : TIPO_COLORS[l.tipo] }}>
                      {l.tipo === 'TRANSFERENCIA' && l.transferDirecao === 'saida' ? '-' : ''}
                      {formatCurrency(l.valor)}
                    </td>
                    <td className="px-4 py-3"><StatusBadge type="status" value={statusEfetivo(l)} /></td>
                    <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs">{l.formaPagamento}</td>
                    <td className="px-4 py-3">
                      {l.carteiraNome ? (
                        <span
                          className="inline-flex items-center justify-center w-44 px-2 py-0.5 rounded-full text-white font-semibold whitespace-nowrap truncate"
                          style={{ background: l.carteiraCor || '#64748b', fontSize: '10px' }}
                        >
                          {l.carteiraNome}
                        </span>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {l.tipo !== 'TRANSFERENCIA' && (
                          <button
                            onClick={() => { setEditLanc(l); setShowModal(true); }}
                            className="p-1.5 rounded-lg hover:bg-blue-50 text-slate-400 hover:text-[#0147a0] transition-colors"
                            data-testid={`edit-lancamento-${l.id}`}
                            title="Editar"
                          >
                            <Edit2 size={14} />
                          </button>
                        )}
                        <button
                          onClick={() => setDeleteConfirm(l.id)}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                          data-testid={`delete-lancamento-${l.id}`}
                          title={l.transferId ? 'Excluir transferência (ambos os lados)' : 'Excluir'}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {lancamentos.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 sm:px-6 py-4 border-t border-slate-100 dark:border-slate-800" data-testid="pagination">
            <p className="text-xs text-slate-400">
              Exibindo {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, lancamentos.length)} de {lancamentos.length}
            </p>
            <div className="flex items-center gap-1 flex-wrap justify-center">
              <button
                onClick={() => setPage(1)}
                disabled={page === 1}
                className="px-2 py-1 rounded-lg text-xs disabled:opacity-40 hover:bg-slate-100 transition-colors"
                data-testid="pagination-first"
              >
                «
              </button>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1 rounded-lg disabled:opacity-40 hover:bg-slate-100 transition-colors"
                data-testid="pagination-prev"
              >
                <ChevronLeft size={14} />
              </button>
              {pageNumbers().map(n => (
                <button
                  key={n}
                  onClick={() => setPage(n)}
                  data-testid={`pagination-page-${n}`}
                  className={`w-7 h-7 rounded-lg text-xs font-medium transition-colors ${
                    n === page ? 'bg-[#0147a0] text-white' : 'hover:bg-slate-100 text-slate-600'
                  }`}
                >
                  {n}
                </button>
              ))}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1 rounded-lg disabled:opacity-40 hover:bg-slate-100 transition-colors"
                data-testid="pagination-next"
              >
                <ChevronRight size={14} />
              </button>
              <button
                onClick={() => setPage(totalPages)}
                disabled={page === totalPages}
                className="px-2 py-1 rounded-lg text-xs disabled:opacity-40 hover:bg-slate-100 transition-colors"
                data-testid="pagination-last"
              >
                »
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)' }}
        >
          <div className="nexus-modal p-6 w-full max-w-sm">
            <h3 className="font-bold text-slate-800 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Confirmar exclusão</h3>
            <p className="text-sm text-slate-500 mb-4">
              {lancamentos.find(l => l.id === deleteConfirm)?.transferId
                ? 'Esta é uma transferência. Ambos os lados (saída e entrada) serão excluídos. Esta ação não pode ser desfeita.'
                : 'Tem certeza que deseja excluir este lançamento? Esta ação não pode ser desfeita.'
              }
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="fin-btn-secondary flex-1 justify-center" data-testid="cancel-delete-btn">
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 justify-center bg-red-500 hover:bg-red-600 text-white rounded-xl px-4 py-2 text-sm font-semibold transition-colors flex items-center"
                data-testid="confirm-delete-btn"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      <LancamentoModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditLanc(null); }}
        lancamento={editLanc}
        onSuccess={fetchLancamentos}
      />

      {/* Import CSV Modal */}
      <ImportacaoCSVModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImported={fetchLancamentos}
      />

      {/* Transferência Modal */}
      {showTransferenciaModal && (
        <TransferenciaModal
          carteiras={carteiras}
          onClose={() => setShowTransferenciaModal(false)}
          onSaved={() => {
            // Clear date filters so the new transfer (today) always appears,
            // then let the useEffect re-trigger fetchLancamentos with updated filters
            setFilters(p => ({ ...p, data_inicio: '', data_fim: '' }));
          }}
        />
      )}
    </div>
  );
}
